import type { IntakeFormListItem } from '@/definitions/intakeForm'

/**
 * What the action available on a form is called.
 *
 * Deactivate and Reactivate rather than the endpoints' own disable and enable, so
 * the action reads as the inverse of the Active / Deactivated status beside it.
 */
export const intakeFormEnabledActionLabel = (isEnabled: boolean): string =>
  isEnabled ? 'Deactivate' : 'Reactivate'

export interface IntakeFormDeactivationCopy {
  title: string
  /** What patients get once the form is off. */
  effect: string
  /** Present only when the form is still being sent to patients. */
  inUseNote?: string
  /** What happens to intake reminders already queued, and to new bookings. */
  remindersNote: string
  /** That it can be switched back on, which keeps the warning proportionate. */
  reversal: string
}

const listNames = (names: string[]): string =>
  new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(names)

const describeUsage = (
  form: IntakeFormListItem,
  sendsIntakeReminders: boolean
): string | undefined => {
  const uses: string[] = []
  const templates = form.used_by_text_templates
  if (templates.length > 0) {
    const noun = templates.length === 1 ? 'text template carries' : 'text templates carry'
    uses.push(`the ${listNames(templates)} ${noun} its link`)
  }
  const types = form.used_by_appointment_types
  if (types.length > 0) {
    const plural = types.length !== 1
    const linked = sendsIntakeReminders
      ? `${plural ? 'send' : 'sends'} it in reminder texts`
      : `${plural ? 'point' : 'points'} to it`
    uses.push(`the ${listNames(types)} appointment ${plural ? 'types' : 'type'} ${linked}`)
  }
  return uses.length > 0 ? `It is still in use: ${uses.join(', and ')}.` : undefined
}

// Shown whatever the current settings: a queued reminder carries the link as text from
// when it was scheduled, and switching intake automation off does not cancel it.
const describeReminders = (
  form: IntakeFormListItem,
  sendsIntakeReminders: boolean
): string => {
  const queued =
    'Intake reminders already queued for booked appointments may still carry this form’s ' +
    'link, and it will show “Form not found”.'
  return sendsIntakeReminders && form.used_by_appointment_types.length > 0
    ? `${queued} Appointments booked or changed while it is off get no intake reminder, ` +
        'and reactivating it does not send the ones skipped.'
    : queued
}

/**
 * The question asked before a form is deactivated.
 *
 * A form still in use is named rather than protected — switching it off is
 * allowed, and the operator is the one who knows whether a replacement is coming.
 *
 * Reactivating is not asked about — it puts a working link back rather than
 * taking one away.
 */
export const intakeFormDeactivationCopy = (
  form: IntakeFormListItem,
  /** The organization's intake automation: without it no new intake reminder is scheduled. */
  sendsIntakeReminders: boolean
): IntakeFormDeactivationCopy => ({
  title: `Deactivate ${form.display_name}?`,
  effect:
    'Patients who open this form’s link will see “Form not found” instead of the ' +
    'form, including links already sent.',
  inUseNote: describeUsage(form, sendsIntakeReminders),
  remindersNote: describeReminders(form, sendsIntakeReminders),
  reversal: 'Reactivating it from this tab makes its link work again, links already sent included.',
})

/** The line confirming a change that went through. */
export const intakeFormEnabledSuccessMessage = (
  form: IntakeFormListItem,
  isEnabled: boolean
): string =>
  isEnabled
    ? `${form.display_name} is active again.`
    : `${form.display_name} is deactivated. Patients can no longer open it.`
